import moment from 'moment';
import {
	createSchedulerMetric,
	getSchedulerMetrics,
	SchedulerMetric,
	updateSchedulerMetric,
} from 'core/API/scheduler';

export interface ShiftLike {
	startAt?: string;
	endAt?: string;
	start?: Date | string;
	end?: Date | string;
	employee?: { id?: number | string } | string | null;
	active?: boolean;
	deleted?: boolean;
}

export function getShiftStartAt(shift: ShiftLike): string | null {
	if (shift.startAt) return shift.startAt;
	if (shift.start) return moment(shift.start).format('YYYY-MM-DDTHH:mm:ss');
	return null;
}

export function getShiftEndAt(shift: ShiftLike): string | null {
	if (shift.endAt) return shift.endAt;
	if (shift.end) return moment(shift.end).format('YYYY-MM-DDTHH:mm:ss');
	return null;
}

export function getShiftDurationHours(shift: ShiftLike): number {
	const startAt = getShiftStartAt(shift);
	const endAt = getShiftEndAt(shift);
	if (!startAt || !endAt) return 0;

	const hours = moment(endAt).diff(moment(startAt), 'hours', true);
	return hours > 0 ? hours : 0;
}

export function isShiftActive(shift: ShiftLike): boolean {
	if (shift.deleted) return false;
	if (shift.active === false) return false;
	return true;
}

export function formatMetricDate(date: string | Date): string {
	return moment(date).format('YYYY-MM-DD');
}

export function getMetricDay(metric: { day?: string | { '@id'?: string } }): string | null {
	const { day } = metric;
	if (!day) return null;

	if (typeof day === 'string') {
		return formatMetricDate(day);
	}

	if (typeof day === 'object' && day['@id']) {
		const datePart = day['@id'].split('/').pop();
		return datePart ? formatMetricDate(datePart) : null;
	}

	return null;
}

export function calculateSplh(sales: number, hoursScheduled: number): number {
	return hoursScheduled > 0 ? sales / hoursScheduled : 0;
}

export function calculateCplh(customers: number, hoursScheduled: number): number {
	return hoursScheduled > 0 ? customers / hoursScheduled : 0;
}

export function calculateDayHoursFromShifts(
	shifts: ShiftLike[],
	dayDate: string,
	dailyOTThreshold = 8
): { hoursScheduled: number; otHoursScheduled: number } {
	const normalizedDay = formatMetricDate(dayDate);

	const dayShifts = shifts.filter((shift) => {
		if (!isShiftActive(shift)) return false;

		const startAt = getShiftStartAt(shift);
		if (!startAt) return false;

		return formatMetricDate(startAt) === normalizedDay;
	});

	let hoursScheduled = 0;
	const employeeDailyHours = new Map<string, number>();

	dayShifts.forEach((shift) => {
		const hours = getShiftDurationHours(shift);
		hoursScheduled += hours;

		const employeeId =
			typeof shift.employee === 'object' && shift.employee !== null
				? shift.employee.id?.toString()
				: null;

		if (employeeId) {
			employeeDailyHours.set(employeeId, (employeeDailyHours.get(employeeId) || 0) + hours);
		}
	});

	let otHoursScheduled = 0;
	employeeDailyHours.forEach((hoursWorked) => {
		if (hoursWorked > dailyOTThreshold) {
			otHoursScheduled += hoursWorked - dailyOTThreshold;
		}
	});

	return {
		hoursScheduled: Math.round(hoursScheduled * 100) / 100,
		otHoursScheduled: Math.round(otHoursScheduled * 100) / 100,
	};
}

export function collectShiftMetricDates(...dateCandidates: Array<string | Date | null | undefined>): string[] {
	const uniqueDates = new Set<string>();

	dateCandidates.forEach((candidate) => {
		if (!candidate) return;
		uniqueDates.add(formatMetricDate(candidate));
	});

	return Array.from(uniqueDates);
}

export function findMetricForDay(metrics: SchedulerMetric[], dayDate: string): SchedulerMetric | undefined {
	const normalizedDay = formatMetricDate(dayDate);
	return metrics.find((metric) => getMetricDay(metric) === normalizedDay);
}

export function buildSchedulerMetricPayload(args: {
	workplaceId: number | string;
	day: string;
	sales: number;
	customers: number;
	hoursScheduled: number;
	otHoursScheduled: number;
}) {
	const sales = Math.round(args.sales * 100) / 100;
	const customers = Math.round(args.customers * 100) / 100;
	const hoursScheduled = Math.round(args.hoursScheduled * 100) / 100;
	const otHoursScheduled = Math.round(args.otHoursScheduled * 100) / 100;

	return {
		workplace: `api/workplaces/${args.workplaceId}`,
		day: formatMetricDate(args.day),
		sales,
		customers,
		hoursScheduled,
		otHoursScheduled,
		splh: calculateSplh(sales, hoursScheduled),
		cplh: calculateCplh(customers, hoursScheduled),
	};
}

export async function syncSchedulerMetricsForDates(args: {
	workplaceId: number;
	dates: Array<string | Date | null | undefined>;
	shifts: ShiftLike[];
	dailyOTThreshold?: number;
}): Promise<void> {
	const { workplaceId, shifts, dailyOTThreshold = 8 } = args;
	const normalizedDates = collectShiftMetricDates(...args.dates);
	if (!workplaceId || normalizedDates.length === 0) return;

	const sortedDates = [...normalizedDates].sort();
	const existingMetrics = await getSchedulerMetrics(workplaceId, sortedDates[0], sortedDates[sortedDates.length - 1]);

	for (const day of normalizedDates) {
		const existingMetric = findMetricForDay(existingMetrics, day);
		const { hoursScheduled, otHoursScheduled } = calculateDayHoursFromShifts(
			shifts,
			day,
			dailyOTThreshold
		);

		const sales = existingMetric?.sales ?? 0;
		const customers = existingMetric?.customers ?? 0;

		const payload = buildSchedulerMetricPayload({
			workplaceId,
			day,
			sales,
			customers,
			hoursScheduled,
			otHoursScheduled,
		});

		if (existingMetric?.id) {
			await updateSchedulerMetric(existingMetric.id, payload);
			continue;
		}

		if (hoursScheduled > 0 || sales > 0 || customers > 0) {
			await createSchedulerMetric(payload);
		}
	}
}
